import {
    Cable,
    CalendarHeart,
    CalendarPlus,
    CircuitBoard,
    ListChecks,
    MapPin,
    MonitorPlay,
    Network,
    Newspaper,
    NotebookTabs,
    Rocket,
    Search,
    Send,
    Tickets,
} from 'lucide-react';
import { ColorScheme } from '@/types/features';
import { GearScoreCategoryItem } from '@/types/gears';
import { InstallerTabCategoryItem } from '@/types/installer';
import { LabTabCategoryItem } from '@/types/labs';
import { MagazineSaleStatusItem } from '@/types/magazines';
import { BreadcrumbItem, ServiceItem } from '@/types/services';

/** URL ───────────────────────────────────────── */

/**
 * auth view (指定のパスは要認証)
 * 指定のURLから開始されるURLはすべて該当
 */
export const AUTH_ROUTES = [
    '/ticket',
    '/store',
    '/gears/list',
    '/gears/list/owned',
    '/gears/settings',
    '/gears/owned',
    '/labs/settings',
    '/labs/owned',
    '/festa/owned',
    '/installer/ide',
];

/** plain view (ヘッダ・フッタ無し） */
export const PLAIN_VIEW_ROUTES: string[] = ['/policy/privacy-policy', '/policy/terms-of-service'];

/** entry view (ヘッダ・フッタ・背景無し） 主に外部からの導線に使用 */
export const ENTRY_VIEW_ROUTES: string[] = [
    '/inquiry',
    '/inquiry/form/support',
    '/inquiry/form/business',
    '/inquiry/form/thanks',
    '/festa-entry/form/*',
    '/festa-entry/cancel',
    '/festa-entry/check-in',
    '/faq',
    '/mail/unsubscribe',
    '/boards/monitor/*',
];

export const SERVICE_ITEMS: readonly ServiceItem[] = [
    {
        label: 'FIND.',
        jp: 'みつける',
        path: '/map',
        match: ['/hubs'],
        Icon: Search,
        mobileVisible: true,
        subItems: [
            {
                label: 'CASE.',
                jp: 'ケーススタディ',
                Icon: Newspaper,
                items: [{ label: 'cases', jp: '活用事例をみつける', path: '/hubs/cases' }],
            },
            {
                label: 'MAP.',
                jp: 'スクーミーハブマップ',
                Icon: MapPin,
                items: [{ label: 'map', jp: '導入事例をみつける', path: '/hubs/map' }],
            },
        ],
    },
    {
        label: 'MAKE.',
        jp: 'つくる',
        path: '/make',
        match: ['/boards', '/installer'],
        Icon: CircuitBoard,
        mobileVisible: false,
        subItems: [
            {
                label: 'BOARD.',
                jp: 'スクーミーボード',
                Icon: CircuitBoard,
                items: [
                    {
                        label: 'editor',
                        jp: 'ブロックエディター',
                        path: '/boards/editor',
                        disableOn: ['mobile'],
                    },
                ],
            },
            {
                label: 'INSTALLER.',
                jp: 'インストーラー',
                Icon: Tickets,
                items: [
                    {
                        label: 'installer',
                        jp: 'ブロックエディター・デスクトップ',
                        path: '/installer/ide',
                        disableOn: ['mobile'],
                    },
                    {
                        label: 'installer',
                        jp: 'スクーミーブリッジ',
                        path: '/installer/bridge',
                        disableOn: ['mobile'],
                    },
                ],
            },
        ],
    },
    {
        label: 'BUILD.',
        jp: 'とりくむ',
        path: '/build',
        match: ['/labs', '/gears'],
        Icon: Rocket,
        mobileVisible: true,
        subItems: [
            {
                label: 'LAB.',
                jp: 'スクーマーズラボ',
                Icon: MonitorPlay,
                items: [
                    { label: 'list', jp: 'スクーマーズラボをみる', path: '/labs/list' },
                    { label: 'register', jp: 'スクーマーラボをつくる', path: '/labs/settings/register/new' },
                ],
            },
            {
                label: 'GEAR.',
                jp: 'スクーミーギア',
                Icon: MonitorPlay,
                items: [
                    { label: 'list', jp: 'スクーミーギアをみる', path: '/gears/list' },
                    { label: 'register', jp: 'スクーミーギアをつくる', path: '/gears/settings/register/new' },
                ],
            },
        ],
    },
    {
        label: 'GUIDE.',
        jp: 'はじめる',
        path: '/guide',
        match: ['/magazines', '/hello-schoomy', '/spot-live'],
        Icon: NotebookTabs,
        mobileVisible: true,
        subItems: [
            {
                label: 'LINEUP.',
                jp: 'ラインナップ',
                Icon: Tickets,
                items: [
                    {
                        label: 'dive',
                        jp: '月刊みんなのダイブ',
                        path: '/magazines/dive',
                    },
                ],
            },
            {
                label: 'HELLO.',
                jp: 'ハロースクーミー',
                Icon: Tickets,
                items: [
                    {
                        label: 'ide-guide',
                        jp: 'スクーミーIDEの使い方',
                        path: '/hello-schoomy/ide-guide/s1',
                    },
                    {
                        label: 'api-guide',
                        jp: 'スクーミーAPIの使い方',
                        path: '/hello-schoomy/api-guide/s1',
                    },
                    {
                        label: 'schoomy-world-guide',
                        jp: 'スクーミーワールドの使い方',
                        path: '/hello-schoomy/schoomy-world-guide/s1',
                    },
                    {
                        label: 'unity-guide',
                        jp: 'Unityでゲームを作ろう',
                        path: '/hello-schoomy/unity-guide/s1',
                    },
                ],
            },
            {
                label: 'LIVE.',
                jp: 'スポットライブ',
                Icon: Tickets,
                items: [
                    {
                        label: 'connector-guide',
                        jp: 'コネクターの使い方',
                        path: '/spot-live/connector-guide',
                    },
                    { label: 'problem-solving', jp: '課題解決のはじめ方', path: '/spot-live/problem-solving' },
                ],
            },
        ],
    },
] as const;

export const SERVICE_AUTH_ME_ITEMS: readonly ServiceItem[] = [
    {
        label: 'FESTA.',
        jp: 'マイフェスタ',
        path: '/festa/owned',
        match: ['/festa'],
        Icon: CalendarHeart,
        subItems: [],
    },
    {
        label: 'MY GEAR.',
        jp: 'マイギア',
        path: '/gears/owned',
        match: ['/gears'],
        Icon: MonitorPlay,
        subItems: [],
    },
    {
        label: 'MY LAB.',
        jp: 'マイラボ',
        path: '/labs/owned',
        match: ['/labs'],
        Icon: Network,
        subItems: [],
    },
];

export const SERVICE_SUPPORT_ITEMS: readonly ServiceItem[] = [
    {
        label: 'ENTRY.',
        jp: 'フェスタエントリー',
        path: '/festa-entry/form',
        match: ['/festa-entry'],
        Icon: CalendarPlus,
        subItems: [],
    },
    {
        label: 'FAQ.',
        jp: 'よくあるご質問',
        path: '/faq',
        match: ['/faq'],
        Icon: ListChecks,
        subItems: [],
    },
    {
        label: 'INQUIRY.',
        jp: 'お問い合わせ',
        path: '/inquiry',
        match: ['/inquiry'],
        Icon: Send,
        subItems: [],
    },
];

export const BREADCRUMB_ITEMS: Record<string, BreadcrumbItem[]> = {
    '/hubs/map': [{ label: 'スクーミーマップ' }],
    '/hubs/cases': [{ label: '活用事例', href: '/hubs/cases' }],
    '/announcements': [{ label: 'お知らせ' }],
    '/installer/ide': [{ label: 'IDEインストーラー&リリースノート' }],
    '/magazines/dive': [{ label: '月刊みんなのダイブ', href: '/magazines' }],
    '/magazines/dive/detail': [{ label: '月刊みんなのダイブ', href: '/magazines/dive' }],
    '/magazines/dive/series': [{ label: '月刊みんなのダイブ', href: '/magazines/dive' }],
    '/magazines/dive/stage': [{ label: '月刊みんなのダイブ - ステージ', href: '/magazines/dive/stage' }],
    '/magazines/dive/stage/detail': [{ label: '月刊みんなのダイブ - ステージ', href: '/magazines/dive/stage' }],
    '/magazines/dive/stage/series': [{ label: '月刊みんなのダイブ - ステージ', href: '/magazines/dive/stage' }],
    '/gears/list': [{ label: 'ギアライブラリ' }],
    '/gears/detail': [{ label: 'ギアライブラリ', href: '/gears/list' }, { label: 'ギア詳細' }],
    '/gears/settings': [{ label: 'ギア作成・編集' }],
    '/gears/settings/register/new': [{ label: 'ギア作成・編集', href: '/gears/settings' }],
    '/gears/settings/[gearId]': [{ label: 'ギア作成・編集' }],
    '/gears/settings/register/lab': [{ label: 'ギア作成・編集', href: '/gears/settings' }, { label: 'ギア制作ラボ' }],
    '/gears/settings/register/meta': [{ label: 'ギア作成・編集', href: '/gears/settings' }, { label: 'コンセプト' }],
    '/gears/settings/register/resource': [{ label: 'ギア作成・編集', href: '/gears/settings' }, { label: '動画' }],
    '/gears/settings/register/code': [
        { label: 'ギア作成・編集', href: '/gears/settings' },
        { label: 'コードファイル' },
    ],
    '/gears/settings/edit/lab': [{ label: 'ギア作成・編集', href: '/gears/settings' }, { label: 'ギア制作ラボ' }],
    '/gears/settings/edit/meta': [{ label: 'ギア作成・編集', href: '/gears/settings' }, { label: 'コンセプト' }],
    '/gears/settings/edit/resource': [{ label: 'ギア作成・編集', href: '/gears/settings' }, { label: '動画' }],
    '/gears/settings/edit/code': [{ label: 'ギア作成・編集', href: '/gears/settings' }, { label: 'コードファイル' }],
    '/gears/owned': [{ label: 'マイギア' }],
    '/labs/list': [{ label: 'ラボリスト' }],
    '/labs/detail': [{ label: 'ラボリスト', href: '/labs/list' }, { label: 'ラボ詳細' }],
    '/labs/settings': [{ label: 'ラボ作成・編集' }],
    '/labs/settings/register/meta': [{ label: 'ラボ作成・編集', href: '/labs/settings' }, { label: '基本情報設定' }],
    '/labs/settings/register/resource': [
        { label: 'ラボ作成・編集', href: '/labs/settings' },
        { label: 'デザイン設定' },
    ],
    '/labs/settings/register/member': [{ label: 'ラボ作成・編集', href: '/labs/settings' }, { label: 'メンバー設定' }],
    '/labs/settings/edit/meta': [{ label: 'ラボ作成・編集', href: '/labs/settings' }, { label: '基本情報設定' }],
    '/labs/settings/edit/resource': [{ label: 'ラボ作成・編集', href: '/labs/settings' }, { label: 'デザイン設定' }],
    '/labs/settings/edit/member': [{ label: 'ラボ作成・編集', href: '/labs/settings' }, { label: 'メンバー設定' }],
    '/labs/owned': [{ label: 'マイラボ' }],
    '/festa/owned': [{ label: 'マイフェスタ' }],
    '/tickets': [{ label: 'クラスチケット' }],
    '/tickets/owned': [{ label: 'マイチケ' }],
    '/me/device': [{ label: 'デバイス' }],
    '/inquiry': [{ label: 'お問い合わせ' }],
    '/inquiry/form/support': [{ label: 'お問い合わせ', href: '/inquiry' }, { label: 'サポート' }],
    '/inquiry/form/business': [{ label: 'お問い合わせ', href: '/inquiry' }, { label: '法人の方へ' }],
    '/inquiry/form/thanks': [{ label: 'お問い合わせ', href: '/inquiry' }, { label: '送信完了' }],
    '/policy/privacy-policy': [{ label: 'プライバシーポリシー' }],
    '/policy/terms-of-service': [{ label: '利用規約' }],
    '/stories': [{ label: 'ストーリーズ', href: '/stories' }],
    '/stories/detail': [{ label: 'ストーリーズ', href: '/stories' }],
};

/** UI ───────────────────────────────────────── */

export const backgroundColor: readonly ColorScheme[] = [
    {
        bg: 'bg-color-theme-default',
        bgText: 'text-color-theme-default-bg',
        text: 'text-color-theme-default',
        bgBorder: 'border-color-theme-default-bg',
        border: 'border-color-theme-default',
    },
    {
        bg: 'bg-color-theme-red',
        bgText: 'text-color-theme-red-bg',
        text: 'text-color-theme-red',
        bgBorder: 'border-color-theme-red-bg',
        border: 'border-color-theme-red',
    },
    {
        bg: 'bg-color-theme-pink',
        bgText: 'text-color-theme-pink-bg',
        text: 'text-color-theme-pink',
        bgBorder: 'border-color-theme-pink-bg',
        border: 'border-color-theme-pink',
    },
    {
        bg: 'bg-color-theme-yello',
        bgText: 'text-color-theme-yello-bg',
        text: 'text-color-theme-yello',
        bgBorder: 'border-color-theme-yello-bg',
        border: 'border-color-theme-yello',
    },
    {
        bg: 'bg-color-theme-orange',
        bgText: 'text-color-theme-orange-bg',
        text: 'text-color-theme-orange',
        bgBorder: 'border-color-theme-orange-bg',
        border: 'border-color-theme-orange',
    },
    {
        bg: 'bg-color-theme-green',
        bgText: 'text-color-theme-green-bg',
        text: 'text-color-theme-green',
        bgBorder: 'border-color-theme-green-bg',
        border: 'border-color-theme-green',
    },
    {
        bg: 'bg-color-theme-blue',
        bgText: 'text-color-theme-blue-bg',
        text: 'text-color-theme-blue',
        bgBorder: 'border-color-theme-blue-bg',
        border: 'border-color-theme-blue',
    },
    {
        bg: 'bg-color-theme-black',
        bgText: 'text-color-theme-black-bg',
        text: 'text-color-theme-black',
        bgBorder: 'border-color-theme-black-bg',
        border: 'border-color-theme-black',
    },
    {
        bg: 'bg-color-theme-s1',
        bgText: 'text-color-theme-s1-bg',
        text: 'text-color-theme-s1',
        bgBorder: 'border-color-theme-s1-bg',
        border: 'border-color-theme-s1',
    },
    {
        bg: 'bg-color-theme-s2',
        bgText: 'text-color-theme-s2-bg',
        text: 'text-color-theme-s2',
        bgBorder: 'border-color-theme-s2-bg',
        border: 'border-color-theme-s2',
    },
    {
        bg: 'bg-color-theme-s3',
        bgText: 'text-color-theme-s3-bg',
        text: 'text-color-theme-s3',
        bgBorder: 'border-color-theme-s3-bg',
        border: 'border-color-theme-s3',
    },
    {
        bg: 'bg-color-theme-s4',
        bgText: 'text-color-theme-s4-bg',
        text: 'text-color-theme-s4',
        bgBorder: 'border-color-theme-s4-bg',
        border: 'border-color-theme-s4',
    },
    {
        bg: 'bg-color-theme-s5',
        bgText: 'text-color-theme-s5-bg',
        text: 'text-color-theme-s5',
        bgBorder: 'border-color-theme-s5-bg',
        border: 'border-color-theme-s5',
    },
    {
        bg: 'bg-color-theme-s6',
        bgText: 'text-color-theme-s6-bg',
        text: 'text-color-theme-s6',
        bgBorder: 'border-color-theme-s6-bg',
        border: 'border-color-theme-s6',
    },
    {
        bg: 'bg-color-theme-s7',
        bgText: 'text-color-theme-s7-bg',
        text: 'text-color-theme-s7',
        bgBorder: 'border-color-theme-s7-bg',
        border: 'border-color-theme-s7',
    },
    {
        bg: 'bg-color-theme-s8',
        bgText: 'text-color-theme-s8-bg',
        text: 'text-color-theme-s8',
        bgBorder: 'border-color-theme-s8-bg',
        border: 'border-color-theme-s8',
    },
    {
        bg: 'bg-color-theme-s9',
        bgText: 'text-color-theme-s9-bg',
        text: 'text-color-theme-s9',
        bgBorder: 'border-color-theme-s9-bg',
        border: 'border-color-theme-s9',
    },
    {
        bg: 'bg-color-theme-s0',
        bgText: 'text-color-theme-s0-bg',
        text: 'text-color-theme-s0',
        bgBorder: 'border-color-theme-s0-bg',
        border: 'border-color-theme-s0',
    },
] as const;

/** service ────────────────────────────────────── */

/** profile */

export const genderList = [
    { label: '指定しない', value: '09' },
    { label: '男性', value: '01' },
    { label: '女性', value: '02' },
];

/** gears */

export const GEAR_SCORE_CATEGORIES: GearScoreCategoryItem[] = [
    { label: '日常的', field: 'score_daily', graphIndex: 0 },
    { label: '専門的', field: 'score_specialized', graphIndex: 3 },
    { label: 'ソフトウェア', field: 'score_software', graphIndex: 1 },
    { label: 'ハードウェア', field: 'score_hardware', graphIndex: 4 },
    { label: '革新的', field: 'score_innovation', graphIndex: 5 },
    { label: '一般的', field: 'score_versatile', graphIndex: 2 },
];

/** labs */

export const LAB_TAB_CATEGORIES: LabTabCategoryItem[] = [
    { type: 'general', label: 'スクーマーズラボ' },
    { type: 'official', label: '公式ラボ' },
    { type: 'certified', label: '認定ラボ' },
];

export const INSTALLER_TAB_CATEGORIES: InstallerTabCategoryItem[] = [
    { type: 'ide_desktop', label: '[デスクトップ版] スクーミーIDE', icon: '/images/installer/ide-icon.png' },
    { type: 'bridge', label: 'スクーミーブリッジ', icon: '/images/installer/bridge-icon.png' },
];

/** magazines */

export const MAGAZINE_SALE_STATUS_LIST: MagazineSaleStatusItem[] = [
    { value: 'pre_sale', label: '発売前' },
    { value: 'on_sale', label: '販売中' },
    { value: 'end_sale', label: '販売終了' },
];

export const LAB_MEMBER_ROLES = {
    leader: {
        label: 'リーダー',
        canUpload: false,
        selectable: false,
    },
    programmer: {
        label: 'プログラマー',
        canUpload: true,
        selectable: true,
    },
    designer: {
        label: 'デザイナー',
        canUpload: true,
        selectable: true,
    },
    tester: {
        label: 'テスター',
        canUpload: true,
        selectable: true,
    },
    observer: {
        label: 'オブザーバー',
        canUpload: true,
        selectable: true,
    },
    supporter: {
        label: 'サポーター',
        canUpload: true,
        selectable: true,
    },
} as const;

export const DEV_SERVER_API_URL = process.env.DEV_SERVER_API_URL;
export const API_URL = process.env.NEXT_PUBLIC_API_URL;
export const WS_URL = process.env.NEXT_PUBLIC_WS_URL;
export const WS_BRIDGE_API_URL = process.env.NEXT_PUBLIC_WS_URL_BRIDGE_API;
export const WS_LOCALHOST_URL = process.env.NEXT_PUBLIC_LOCALHOST_URL;
export const STRIPE_PUBLIC_KEY = process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY;
export const WORLD_WEBSITE_URL = process.env.NEXT_PUBLIC_WORLD_WEBSITE_URL;
export const OFFICIAL_WEBSITE_URL = process.env.NEXT_PUBLIC_OFFICIAL_WEBSITE_URL;
export const SNS_YOUTUBE_URL = process.env.NEXT_PUBLIC_SNS_YOUTUBE_URL;
export const SNS_INSTAGRAM_URL = process.env.NEXT_PUBLIC_SNS_INSTAGRAM_URL;
export const SNS_TWITTER_URL = process.env.NEXT_PUBLIC_SNS_TWITTER_URL;
export const SNS_FACEBOOK_URL = process.env.NEXT_PUBLIC_SNS_FACEBOOK_URL;
export const SNS_LINE_URL = process.env.NEXT_PUBLIC_SNS_LINE_URL;
export const SNS_SCHOOMY_URL = process.env.NEXT_PUBLIC_SNS_SCHOOMY_URL;
export const MAPBOX_ACCESS_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;
export const BASIC_AUTH_USER = process.env.NEXT_PUBLIC_BASIC_AUTH_USER;
export const BASIC_AUTH_PASSWORD = process.env.NEXT_PUBLIC_BASIC_AUTH_PASSWORD;
export const NEXT_PUBLIC_BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;

// package.jsonのversionをそのままWebアプリのバージョン表示に使う(設定モーダルの
// バージョン情報・診断情報コピー用)。package.json全体を直接importすると不要な
// package metadataまでクライアントバンドルに含まれるため、next.config.jsのenvで
// versionだけをビルド時定数として注入したものを参照する。
export const WEB_APP_VERSION = process.env.NEXT_PUBLIC_APP_VERSION ?? '';

// このエディターが正常動作を確認しているブリッジの最低バージョン。ブリッジ側の
// bridge/constants.pyのBRIDGE_VERSIONと突き合わせて、古い場合に注意喚起するために使う。
// 1.2.0でchip_id(read_chip_id)/per-portロック等に対応。これより古いブリッジはchip_id
// 問い合わせに応答しないため、BoardToolbar.tsxのisUnknownBlueBoard判定で区別している。
export const REQUIRED_BRIDGE_VERSION = '1.2.0';

// monitor_bluetoothブロックが生成するスケッチが、接続デバイス名を名乗るために送信する行の
// プレフィックス。Web Serial APIパスはこれを検出してデバイス名として扱い(通常ログには
// 出さない)、ブリッジパスは既にOS由来でBT名が分かる(detectedBoards.bluetooth_name)ため
// 単にノイズとして除去するだけに使う。3箇所(コード生成/useMonitorWebSerialApi.ts/store)で
// 値がずれないよう、ここに集約する。
export const BT_NAME_ANNOUNCE_PREFIX = '__SCHOOMY_BT_NAME__:';
